import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { StorageContainerAcl, StoragePolicyResourceRef, StorageResourceAcl, StorageResourceRef } from '../../../../../types/azureStorage';
import { cancelStorageRequest, getStorageAccessPolicies, getStorageContainerAcl, newStorageRequestId, toStorageOperationError } from '../../services/azureStorageApi';
import { storagePolicyAclQueryKey } from '../../services/storageListingKeys';

type ContainerRef = Extract<StorageResourceRef, { kind: 'container' }>;
/** A container's ACL (public access, version, policies) or a table's / queue's (policies only). */
export type StoragePolicyAcl = StorageContainerAcl | StorageResourceAcl;

/** The name that identifies a policy owner within its kind (the query key's memo input). */
function policyOwnerName(resource: StoragePolicyResourceRef): string {
  switch (resource.kind) {
    case 'container': return resource.container;
    case 'table': return resource.table;
    case 'queue': return resource.queue;
    case 'share': return resource.share;
    default: return (resource as { kind: never }).kind;
  }
}

/**
 * The ACL of a policy-bearing resource — a container through the container ACL channel, a table, a queue or (S7) a
 * file share through `get-access-policies` (follow-up 2026-09-25) — with one request id per read, cancelled when the
 * query is aborted or the dialog unmounts. A container's key is the container ACL key, so both hooks share one cache
 * entry.
 */
export function useStoragePolicyAcl(args: { connectionId: string; resource: StoragePolicyResourceRef; enabled: boolean }) {
  const client = useQueryClient();
  const activeCancel = useRef<(() => Promise<void>) | null>(null);
  const resourceKey = policyOwnerName(args.resource);
  const key = useMemo(() => storagePolicyAclQueryKey(args.connectionId, args.resource), [args.connectionId, args.resource.kind, resourceKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = useQuery({
    queryKey: key, enabled: args.enabled, retry: false, staleTime: Infinity, refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<StoragePolicyAcl> => {
      const requestId = newStorageRequestId();
      let cancelled = false;
      const cancel = async () => { if (!cancelled) { cancelled = true; await cancelStorageRequest(requestId); } };
      activeCancel.current = cancel;
      const onAbort = () => void cancel();
      signal.addEventListener('abort', onAbort);
      try {
        const lifecycle = { connectionId: args.connectionId, requestId, timeoutMs: 30_000 };
        return args.resource.kind === 'container'
          ? await getStorageContainerAcl({ ...lifecycle, resource: args.resource })
          : await getStorageAccessPolicies({ ...lifecycle, resource: args.resource });
      } finally {
        signal.removeEventListener('abort', onAbort);
        if (activeCancel.current === cancel) activeCancel.current = null;
      }
    },
  });
  useEffect(() => () => { void activeCancel.current?.(); client.removeQueries({ queryKey: key, exact: true }); }, [client, key]);
  return { data: query.data, isLoading: query.isPending, error: query.error ? toStorageOperationError(query.error) : undefined, reload: () => void client.resetQueries({ queryKey: key, exact: true }) };
}

export function useStorageContainerAcl(args: { connectionId: string; resource: ContainerRef; enabled: boolean }) {
  const acl = useStoragePolicyAcl(args);
  return { ...acl, data: acl.data as StorageContainerAcl | undefined };
}
